
import React from "react";
import "./LBInput.css";

/**
 * LBInput - Linksbridge Design System Input Component
 * Implements the Figma design for a basic text input with label, example, and warning message.
 * Uses project CSS variables and conventions.
 */

const LBInput = ({
	label = "Text Field Name / Header",
	placeholder = "Text here",
	value = "",
	onChange = () => {},
	onFocus,
	onBlur,
	example = "",
	warning = "",
	tooltip = "",
	type = "text",
	className = "",
	disabled = false,
	state,
	id,
	controlId,
	clearable = false,
	...props
}) => {
	const [isFocused, setIsFocused] = React.useState(false);
	const hasValue = value !== null && value !== undefined && String(value).length > 0;
	const hasWarning = Boolean(warning);
	const hasTooltip = Boolean(tooltip);
	const resolvedInputId = id || controlId;
	const tooltipId = React.useMemo(() => {
		const baseId = resolvedInputId || "lbinput";
		return `${baseId}-tooltip`;
	}, [resolvedInputId]);

	const normalizedState = state ? String(state).toLowerCase() : "";
	const computedState = disabled
		? "disabled"
		: hasWarning
		? "error"
		: isFocused
		? "focus"
		: hasValue
		? "filled"
		: "default";

	const activeState = ["default", "focus", "filled", "error", "disabled"].includes(normalizedState)
		? normalizedState
		: computedState;

	const stateClassName = `lbinput-${activeState}`;
	const isDisabledState = activeState === "disabled";
	const isErrorState = activeState === "error";

	const handleClear = (e) => {
		if (onChange) {
			onChange({
				...e,
				target: {
					...e.target,
					id: resolvedInputId,
					name: props.name,
					value: "",
				},
			});
		}
	};

	const handleFocus = (e) => {
		setIsFocused(true);
		if (onFocus) {
			onFocus(e);
		}
	};

	const handleBlur = (e) => {
		setIsFocused(false);
		if (onBlur) {
			onBlur(e);
		}
	};

	return (
		<div className={`lbinput-root ${stateClassName} ${className}`} data-name="LBInput-root" data-state={activeState}>
			{label && (
				<div className="lbinput-label-row">
					<label className="lbinput-label" htmlFor={id || controlId}>{label}</label>
					{hasTooltip && (
						<div className="lbinput-tooltip-wrapper">
							<button
								type="button"
								className="lbinput-tooltip-trigger"
								aria-label="Show field help"
								aria-describedby={tooltipId}
								tabIndex={isDisabledState ? -1 : 0}
								disabled={isDisabledState}
							>
								<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
									<circle cx="6" cy="6" r="5.5" stroke={isErrorState ? "#C8303F" : "#7E818D"} />
									<path d="M6 5.1V8" stroke={isErrorState ? "#C8303F" : "#7E818D"} strokeWidth="1.2" strokeLinecap="round" />
									<circle cx="6" cy="3.45" r="0.6" fill={isErrorState ? "#C8303F" : "#7E818D"} />
								</svg>
							</button>
							<div id={tooltipId} role="tooltip" className="lbinput-tooltip-body">{tooltip}</div>
						</div>
					)}
				</div>
			)}
			<div className={`lbinput-box${clearable && hasValue && !isDisabledState ? " lbinput-box--clearable" : ""}`}>
				<input
					{...props}
					id={resolvedInputId}
					type={type}
					className="lbinput-input"
					placeholder={placeholder}
					value={value}
					onChange={onChange}
					onFocus={handleFocus}
					onBlur={handleBlur}
					disabled={isDisabledState}
				/>
				{clearable && hasValue && !isDisabledState && (
					<button
						type="button"
						className="lbinput-clear-btn"
						aria-label="Clear input"
						onClick={handleClear}
					>
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<circle cx="8" cy="8" r="8" fill="#EFEFF1" />
							<path d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5" stroke="#7E818D" strokeWidth="1.5" strokeLinecap="round" />
						</svg>
					</button>
				)}
			</div>
			{example && (
				<div className="lbinput-example">
					<span className="lbinput-example-text">{example}</span>
				</div>
			)}
			{hasWarning && (
				<span className="lbinput-warning">{warning}</span>
			)}
		</div>
	);
};

export default LBInput;
